<template>
    <b-modal
        :visible="value"
        :title="modalTitle"
        size="lg"
        centered
        hide-footer
        no-close-on-backdrop
        modal-class="dev-ticket-modal"
        content-class="dt-modal-content"
        header-class="dt-modal-header"
        @change="onVisibleChange"
    >
        <div v-if="isCreate || localTicket">
            <validation-observer ref="observer">
                <b-row>
                    <!-- Title -->
                    <b-col cols="12" class="mb-1">
                        <label class="dt-label">{{ msg('Title') }} <span class="text-danger">*</span></label>
                        <validation-provider #default="{ errors }" :name="msg('Title')" rules="required">
                            <b-form-input v-model="form.title" :disabled="readonly"
                                          :placeholder="msg('Short summary')" :state="errors.length ? false : null"/>
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-col>

                    <!-- Description -->
                    <b-col cols="12" class="mb-1">
                        <label class="dt-label">{{ msg('Description') }} <span class="text-danger">*</span></label>
                        <validation-provider #default="{ errors }" :name="msg('Description')" rules="required">
                            <b-form-textarea v-model="form.description" rows="4" max-rows="12" :disabled="readonly"
                                             :placeholder="msg('Describe the issue, steps, expected result')"
                                             :state="errors.length ? false : null"/>
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-col>

                    <!-- Type | Module -->
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="dt-label">{{ msg('Type') }} <span class="text-danger">*</span></label>
                        <validation-provider #default="{ errors }" :name="msg('Type')" rules="required">
                            <v-select v-model="form.type" :options="typeOptions" :reduce="o => o.value"
                                      label="label" append-to-body :disabled="readonly" :placeholder="msg('Select type')"/>
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-col>
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="dt-label">{{ msg('Module') }} <span class="text-danger">*</span></label>
                        <validation-provider #default="{ errors }" :name="msg('Module')" rules="required">
                            <v-select v-model="form.module" :options="moduleOptions" :reduce="o => o.value"
                                      label="label" append-to-body :disabled="readonly" :placeholder="msg('Select module')"/>
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-col>

                    <!-- Priority | Participants -->
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="dt-label">{{ msg('Priority') }}</label>
                        <v-select v-model="form.priority" :options="priorityOptions" :reduce="o => o.value"
                                  label="label" append-to-body :clearable="false" :disabled="readonly">
                            <template #option="{ label, variant }">
                                <b-badge :variant="variant" class="dt-opt-badge">{{ label }}</b-badge>
                            </template>
                            <template #selected-option="{ label, variant }">
                                <b-badge :variant="variant" class="dt-opt-badge">{{ label }}</b-badge>
                            </template>
                        </v-select>
                    </b-col>
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="dt-label">{{ msg('Participants') }}</label>
                        <v-select v-model="form.participants" :options="userOptions" :reduce="o => o.id"
                                  label="name" multiple append-to-body :disabled="readonlyParticipants"
                                  :placeholder="msg('Add people to notify')"/>
                    </b-col>

                    <!-- Created By | Assigned To -->
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="dt-label">{{ msg('Created by') }}</label>
                        <b-form-input :value="createdByName" disabled readonly/>
                    </b-col>
                    <b-col cols="12" md="6" class="mb-1">
                        <label class="dt-label">{{ msg('Assigned to') }}</label>
                        <v-select v-model="form.assignedTo" :options="assignedToOptions" :reduce="o => o.value"
                                  label="label" append-to-body :disabled="readonly" :placeholder="msg('Assigned to')"/>
                    </b-col>

                    <!-- Status (existing tickets only) -->
                    <b-col v-if="!isCreate" cols="12" md="6" class="mb-1">
                        <label class="dt-label">{{ msg('Status') }}</label>
                        <v-select v-model="form.status" :options="statusOptions" :reduce="o => o.value"
                                  label="label" append-to-body :clearable="false" :disabled="!canEditTicket">
                            <template #option="{ label, variant }">
                                <b-badge :variant="variant" class="dt-opt-badge">{{ label }}</b-badge>
                            </template>
                            <template #selected-option="{ label, variant }">
                                <b-badge :variant="variant" class="dt-opt-badge">{{ label }}</b-badge>
                            </template>
                        </v-select>
                    </b-col>

                    <!-- Attachments (new tickets only) -->
                    <b-col v-if="isCreate" cols="12" class="mb-1">
                        <label class="dt-label">{{ msg('Attachments') }}</label>
                        <b-form-file v-model="createFiles" multiple
                                     :placeholder="msg('Choose files or drop them here')" :browse-text="msg('Browse')"/>
                    </b-col>
                </b-row>

                <!-- meta line (existing tickets) -->
                <div v-if="!isCreate && localTicket" class="dt-meta-line">
                    <span>{{ msg('Created by') }}: <strong>{{ localTicket.createdByName || ('#' + localTicket.createdBy) }}</strong></span>
                    <span>{{ msg('Creation Time') }}: <strong>{{ formatDate(localTicket.creationTime) }}</strong></span>
                </div>

                <!-- actions -->
                <div class="d-flex justify-content-end mt-1">
                    <b-button variant="outline-secondary" class="mr-1" @click="close">{{ msg('Cancel') }}</b-button>
                    <b-button v-if="isCreate" variant="primary" :disabled="saving" @click="submitCreate">
                        <b-spinner v-if="saving" small class="mr-50"/>{{ msg('Create ticket') }}
                    </b-button>
                    <b-button v-else-if="canSave" variant="primary" :disabled="saving" @click="saveEdit">
                        <b-spinner v-if="saving" small class="mr-50"/>{{ msg('Save') }}
                    </b-button>
                </div>
            </validation-observer>

            <!-- ───────────── Comments (existing tickets) ───────────── -->
            <div v-if="!isCreate && localTicket" class="dt-section dt-comments-wrap">
                <!-- ticket attachments -->
                <div v-if="localTicket.attachments && localTicket.attachments.length" class="mb-1">
                    <span class="dt-section-title">{{ msg('Attachments') }}</span>
                    <div class="dt-attach-grid mt-50">
                        <template v-for="(att, i) in localTicket.attachments">
                            <div v-if="isImage(att)" :key="'img'+i" class="dt-img-thumb" @click="openImage(att)">
                                <img v-if="previewUrls[att.fileName]" :src="previewUrls[att.fileName]" :alt="att.originalName"/>
                                <b-spinner v-else small/>
                            </div>
                            <b-button v-else :key="'btn'+i" variant="light-secondary" size="sm"
                                      class="dt-att-file" @click="download(att)">
                                <feather-icon icon="PaperclipIcon" size="13" class="mr-25"/>{{ att.originalName }}
                            </b-button>
                        </template>
                    </div>
                </div>

                <span class="dt-section-title">
                    {{ msg('Comments') }}
                    <b-badge variant="light-primary" class="ml-25">{{ (localTicket.comments || []).length }}</b-badge>
                </span>

                <div class="dt-comments mt-1">
                    <div v-if="!(localTicket.comments && localTicket.comments.length)" class="dt-empty-comments">
                        {{ msg('No comments yet') }}
                    </div>
                    <div v-for="(c, i) in localTicket.comments" :key="i"
                         class="dt-comment" :class="{ 'dt-comment--mine': isMine(c.userId) }">
                        <div class="dt-avatar">{{ initials(c.userName) }}</div>
                        <div class="dt-comment-body">
                            <div class="dt-comment-head">
                                <span class="dt-comment-author">{{ c.userName || ('#' + c.userId) }}</span>
                                <span class="dt-comment-time">{{ formatDate(c.timestamp) }}</span>
                            </div>
                            <div class="dt-comment-text">{{ c.message }}</div>
                            <div v-if="c.attachments && c.attachments.length" class="dt-attach-grid mt-25">
                                <template v-for="(att, j) in c.attachments">
                                    <div v-if="isImage(att)" :key="'cimg'+j" class="dt-img-thumb dt-img-thumb--sm"
                                         @click="openImage(att)">
                                        <img v-if="previewUrls[att.fileName]" :src="previewUrls[att.fileName]" :alt="att.originalName"/>
                                        <b-spinner v-else small/>
                                    </div>
                                    <b-button v-else :key="'cbtn'+j" variant="flat-secondary" size="sm"
                                              class="dt-att-inline" @click="download(att)">
                                        <feather-icon icon="PaperclipIcon" size="12" class="mr-25"/>{{ att.originalName }}
                                    </b-button>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- add comment -->
                <div class="dt-add-comment mt-1">
                    <b-form-textarea v-model="newComment" rows="2" max-rows="6" :placeholder="msg('Write a comment…')"/>
                    <div class="d-flex justify-content-between align-items-center mt-50">
                        <b-form-file v-model="commentFiles" multiple size="sm" class="dt-comment-file"
                                     :placeholder="msg('Attach files')" :browse-text="msg('Browse')"/>
                        <b-button variant="primary" size="sm" :disabled="submittingComment || !newComment.trim()"
                                  @click="submitComment">
                            <b-spinner v-if="submittingComment" small class="mr-50"/>
                            <feather-icon v-else icon="SendIcon" size="14" class="mr-25"/>{{ msg('Send') }}
                        </b-button>
                    </div>
                </div>

                <!-- status history -->
                <div class="mt-1">
                    <b-button variant="flat-secondary" size="sm" class="px-50" @click="showHistory = !showHistory">
                        <feather-icon :icon="showHistory ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="14" class="mr-25"/>
                        {{ msg('Status history') }}
                    </b-button>
                    <div v-if="showHistory" class="dt-history mt-50">
                        <div v-for="(h, i) in (localTicket.statusHistory || [])" :key="i" class="dt-history-row">
                            <b-badge :variant="badge(statuses, h.status)" class="mr-50">{{ msg(label(statuses, h.status)) }}</b-badge>
                            <span class="dt-history-time">{{ formatDate(h.updateTime) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="text-center py-3">
            <b-spinner/>
        </div>
    </b-modal>
</template>

<script>
import {ValidationObserver, ValidationProvider} from 'vee-validate'
import vSelect from 'vue-select'
import {
    DEV_TICKET_TYPES, DEV_TICKET_STATUSES, DEV_TICKET_PRIORITIES, DEV_TICKET_MODULES,
    labelFor, variantFor,
} from '@/model/devTicket/devTicketEnums'

export default {
    name: 'DevTicketModal',
    components: {ValidationObserver, ValidationProvider, vSelect},
    props: {
        value: {type: Boolean, default: false},
        // null/empty → create mode; otherwise detail/edit mode for this ticket
        ticket: {type: Object, default: null},
    },
    data() {
        return {
            localTicket: null,
            saving: false,
            submittingComment: false,
            showHistory: false,
            newComment: '',
            commentFiles: [],
            createFiles: [],
            // users referenced by the ticket but possibly outside the current
            // branch scope (creator / participants) — merged into the pickers
            extraUsers: [],
            // image attachment previews: { fileName: objectURL }
            previewUrls: {},
            form: this.emptyForm(),
            types: DEV_TICKET_TYPES,
            statuses: DEV_TICKET_STATUSES,
            priorities: DEV_TICKET_PRIORITIES,
            modules: DEV_TICKET_MODULES,
        }
    },
    computed: {
        isCreate() {
            return !(this.ticket && this.ticket.id);
        },
        modalTitle() {
            return this.isCreate ? this.msg('New ticket') : (this.localTicket ? this.localTicket.title : '');
        },
        canEditTicket() {
            return this.$store.getters['user/canEdit']('dev_ticket');
        },
        isAdmin() {
            const p = this.$store.getters['user/myProfile'];
            return (p && p.isAdmin) || this.$store.getters['user/canOperate']('dev_ticket');
        },
        // other fields editable when creating, or when the user can edit the ticket
        readonly() {
            return !this.isCreate && !this.canEditTicket;
        },
        // participants can be changed by anyone with access (backend allows it)
        readonlyParticipants() {
            return false;
        },
        // read-only display of the creator (current user on create, else the ticket's creator)
        createdByName() {
            if (this.isCreate) {
                return this.$store.getters['user/myName'] || (this.myId != null ? '#' + this.myId : '');
            }
            if (!this.localTicket) return '';
            return this.localTicket.createdByName
                || (this.localTicket.createdBy != null ? '#' + this.localTicket.createdBy : '');
        },
        canSave() {
            // editors save everything; non-editors can still persist participants
            return this.canEditTicket || !this.readonlyParticipants;
        },
        storeUsers() {
            return this.$store.getters['devTicket/getAssignableUsers'];
        },
        userOptions() {
            const seen = {};
            const out = [];
            [...this.storeUsers, ...this.extraUsers].forEach((u) => {
                if (u && u.id != null && !seen[u.id]) {
                    seen[u.id] = true;
                    out.push(u);
                }
            });
            return out;
        },
        // fixed Assigned-To groups (stored as the English value in assignedTo)
        assignedToOptions() {
            return [
                {value: 'Administration', label: this.msg('Administration')},
                {value: 'Devs', label: this.msg('Devs')},
                {value: 'Customer Support', label: this.msg('Customer Support')},
            ];
        },
        myId() {
            const p = this.$store.getters['user/myProfile'];
            return p ? p.id : null;
        },
        typeOptions() {
            return this.types.map((o) => ({value: o.value, label: this.msg(o.label), variant: o.variant}));
        },
        priorityOptions() {
            return this.priorities.map((o) => ({value: o.value, label: this.msg(o.label), variant: o.variant}));
        },
        moduleOptions() {
            return this.modules.map((o) => ({value: o.value, label: this.msg(o.label)}));
        },
        statusOptions() {
            return this.statuses.map((o) => ({value: o.value, label: this.msg(o.label), variant: o.variant}));
        },
    },
    watch: {
        value(open) {
            if (open) this.onOpen();
        },
    },
    beforeDestroy() {
        this.revokePreviews();
    },
    methods: {
        emptyForm() {
            return {
                title: '', description: '', type: null, module: null, priority: 'MEDIUM',
                assignedTo: '', participants: [], createdBy: null, status: 'REQUEST',
            };
        },
        onOpen() {
            this.showHistory = false;
            this.newComment = '';
            this.commentFiles = [];
            this.createFiles = [];
            this.extraUsers = [];
            this.revokePreviews();
            if (!this.storeUsers.length) {
                this.$store.dispatch('devTicket/loadAssignableUsers');
            }
            if (this.isCreate) {
                this.localTicket = null;
                this.form = this.emptyForm();
                // Created By is always the current user (seed into the picker so it shows)
                this.form.createdBy = this.myId;
                const myName = this.$store.getters['user/myName'];
                if (this.myId != null) this.extraUsers = [{id: this.myId, name: myName || ('#' + this.myId)}];
            } else {
                this.localTicket = JSON.parse(JSON.stringify(this.ticket));
                this.seedExtraUsers(this.localTicket);
                this.populateForm(this.localTicket);
                this.loadPreviews();
                this.refresh();
            }
        },
        refresh() {
            this.$store.dispatch('devTicket/fetchById', this.ticket.id).then((t) => {
                if (t) {
                    this.localTicket = t;
                    this.seedExtraUsers(t);
                    this.populateForm(t);
                    this.loadPreviews();
                }
            }).catch(() => {});
        },
        // make sure creator (and any participant we have a name for) show in the pickers
        seedExtraUsers(t) {
            const extra = [];
            if (t.createdBy != null && t.createdByName) {
                extra.push({id: t.createdBy, name: t.createdByName});
            }
            this.extraUsers = extra;
        },
        populateForm(t) {
            this.form = {
                title: t.title,
                description: t.description,
                type: t.type,
                module: t.module,
                priority: t.priority || 'MEDIUM',
                assignedTo: t.assignedTo || '',
                participants: Array.isArray(t.participants) ? [...t.participants] : (t.participants ? Object.values(t.participants) : []),
                createdBy: t.createdBy,
                status: t.status,
            };
        },
        onVisibleChange(v) {
            this.$emit('input', v);
        },
        close() {
            this.revokePreviews();
            this.$emit('input', false);
        },
        async submitCreate() {
            const ok = await this.$refs.observer.validate();
            if (!ok || !this.validateRequired()) return;
            this.saving = true;
            try {
                await this.$store.dispatch('devTicket/create', {ticket: this.buildPayload(), files: this.createFiles});
                this.$store.dispatch('notifications/success', {
                    title: this.msg('Dev Tickets'), message: this.msg('Ticket created'),
                }, {root: true});
                this.$emit('saved');
                this.close();
            } finally {
                this.saving = false;
            }
        },
        async saveEdit() {
            if (this.canEditTicket) {
                const ok = await this.$refs.observer.validate();
                if (!ok || !this.validateRequired()) return;
            }
            this.saving = true;
            try {
                const t = await this.$store.dispatch('devTicket/update', {id: this.localTicket.id, ticket: this.buildPayload()});
                if (t) {
                    this.localTicket = t;
                    this.seedExtraUsers(t);
                    this.populateForm(t);
                }
                this.$emit('saved');
                this.close();
            } finally {
                this.saving = false;
            }
        },
        async submitComment() {
            if (!this.newComment.trim()) return;
            this.submittingComment = true;
            try {
                const t = await this.$store.dispatch('devTicket/addComment', {
                    id: this.localTicket.id, message: this.newComment.trim(), files: this.commentFiles,
                });
                if (t) {
                    this.localTicket = t;
                    this.seedExtraUsers(t);
                    // keep current edits but refresh status (auto-transitions on comment)
                    this.form.status = t.status;
                    this.loadPreviews();
                }
                this.newComment = '';
                this.commentFiles = [];
                this.$emit('saved');
            } finally {
                this.submittingComment = false;
            }
        },
        download(att) {
            this.$store.dispatch('devTicket/downloadFile', {
                ticketId: this.localTicket.id, fileName: att.fileName, originalName: att.originalName,
            });
        },
        // ── attachments / image previews ──
        isImage(att) {
            if (!att) return false;
            if (att.contentType && att.contentType.indexOf('image/') === 0) return true;
            const name = (att.originalName || att.fileName || '').toLowerCase();
            return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
        },
        loadPreviews() {
            if (!this.localTicket || !this.localTicket.id) return;
            const atts = [...(this.localTicket.attachments || [])];
            (this.localTicket.comments || []).forEach((c) => (c.attachments || []).forEach((a) => atts.push(a)));
            atts.forEach((att) => {
                if (this.isImage(att) && att.fileName && !this.previewUrls[att.fileName]) {
                    this.getImgUrlFromServer('dev-tickets/file/' + this.localTicket.id + '/' + encodeURIComponent(att.fileName))
                        .then((url) => { this.$set(this.previewUrls, att.fileName, url); })
                        .catch(() => { /* leave it as a download button */ });
                }
            });
        },
        openImage(att) {
            const url = att && this.previewUrls[att.fileName];
            if (url) window.open(url, '_blank');
            else this.download(att);
        },
        revokePreviews() {
            Object.keys(this.previewUrls).forEach((k) => {
                try { URL.revokeObjectURL(this.previewUrls[k]); } catch (e) { /* noop */ }
            });
            this.previewUrls = {};
        },
        validateRequired() {
            const missing = [];
            if (!this.form.title || !this.form.title.trim()) missing.push(this.msg('Title'));
            if (!this.form.description || !this.form.description.trim()) missing.push(this.msg('Description'));
            if (!this.form.type) missing.push(this.msg('Type'));
            if (!this.form.module) missing.push(this.msg('Module'));
            if (missing.length) {
                this.$store.dispatch('notifications/error', {
                    title: this.msg('Dev Tickets'),
                    message: this.msg('Please fill all required fields') + ': ' + missing.join(', '),
                }, {root: true});
                return false;
            }
            return true;
        },
        buildPayload() {
            return {
                title: this.form.title,
                description: this.form.description,
                type: this.form.type,
                module: this.form.module,
                priority: this.form.priority,
                assignedTo: this.form.assignedTo,
                participants: this.form.participants,
                createdBy: this.form.createdBy,
                status: this.form.status,
            };
        },
        // ── display helpers ──
        label(list, v) { return labelFor(list, v); },
        badge(list, v) { return variantFor(list, v); },
        isMine(userId) { return this.myId != null && userId === this.myId; },
        initials(name) {
            if (!name) return '?';
            return name.split(' ').filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('');
        },
        formatDate(value) {
            if (!value) return '';
            let d;
            if (Array.isArray(value)) {
                d = new Date(value[0], (value[1] || 1) - 1, value[2] || 1, value[3] || 0, value[4] || 0, value[5] || 0);
            } else {
                d = new Date(value);
            }
            if (isNaN(d.getTime())) return '';
            return d.toLocaleString(undefined, {year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'});
        },
    },
}
</script>

<style lang="scss" scoped>
.dt-label {
    font-size: .8rem;
    font-weight: 600;
    margin-bottom: .25rem;
    display: block;
    color: #5e5873;
}

.dt-meta-line {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: .5rem;
    font-size: .82rem;
    color: #6e6b7b;
    padding: .25rem 0 .25rem;
}

.dt-section-title {
    font-size: .95rem;
    font-weight: 700;
    color: #5e5873;
}

.dt-comments-wrap {
    border-top: 1px solid #ebe9f1;
    margin-top: 1.25rem;
    padding-top: 1.25rem;
}

.dt-comments {
    max-height: 320px;
    overflow-y: auto;
    padding-right: .25rem;
}

.dt-empty-comments {
    color: #b9b9c3;
    font-style: italic;
    padding: .5rem 0;
}

.dt-comment {
    display: flex;
    gap: .6rem;
    margin-bottom: .85rem;
}

.dt-avatar {
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #7367f0, #9e95f5);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: .8rem;
    font-weight: 700;
}

.dt-comment-body {
    flex: 1;
    background: #f8f8fb;
    border-radius: .5rem;
    padding: .5rem .75rem;
}

.dt-comment--mine .dt-comment-body {
    background: #eef0ff;
}

.dt-comment-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: .15rem;
}

.dt-comment-author {
    font-weight: 700;
    font-size: .85rem;
    color: #5e5873;
}

.dt-comment-time {
    font-size: .72rem;
    color: #b9b9c3;
}

.dt-comment-text {
    color: #6e6b7b;
    white-space: pre-wrap;
    line-height: 1.45;
}

.dt-add-comment {
    border-top: 1px solid #ebe9f1;
    padding-top: .75rem;
}

.dt-history-row {
    display: flex;
    align-items: center;
    padding: .25rem 0;
}

.dt-history-time {
    font-size: .78rem;
    color: #b9b9c3;
}

.dt-att-inline {
    font-size: .75rem;
}

.dt-attach-grid {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: .5rem;
}

.dt-img-thumb {
    width: 92px;
    height: 92px;
    border-radius: .5rem;
    overflow: hidden;
    cursor: pointer;
    border: 1px solid #ebe9f1;
    background: #f8f8fb;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform .12s ease, box-shadow .12s ease;
}

.dt-img-thumb:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(34, 41, 47, .15);
}

.dt-img-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.dt-img-thumb--sm {
    width: 64px;
    height: 64px;
}
</style>

<style>
/* Dark mode. The b-modal content is portaled to <body>, outside this
   component's scoped DOM, so these overrides are unscoped and keyed to the
   modal's content-class (the CatalogItemModal pattern). */
.dark-layout .dt-modal-content,
.dark-layout .dt-modal-content .modal-body,
.dark-layout .dt-modal-header {
    background-color: #283046;
}

.dark-layout .dt-modal-header .modal-title,
.dark-layout .dt-modal-content .dt-label,
.dark-layout .dt-modal-content .dt-section-title,
.dark-layout .dt-modal-content .dt-comment-author {
    color: #d0d2d6;
}

.dark-layout .dt-modal-content .dt-meta-line,
.dark-layout .dt-modal-content .dt-comment-text {
    color: #b4b7bd;
}

.dark-layout .dt-modal-content .dt-comments-wrap,
.dark-layout .dt-modal-content .dt-add-comment {
    border-top-color: #3b4253;
}

.dark-layout .dt-modal-content .dt-comment-body {
    background: #343d55;
}

.dark-layout .dt-modal-content .dt-comment--mine .dt-comment-body {
    background: #3b4663;
}

.dark-layout .dt-modal-content .dt-comment-time,
.dark-layout .dt-modal-content .dt-history-time {
    color: #676d7d;
}

.dark-layout .dt-modal-content .dt-img-thumb {
    border-color: #3b4253;
    background: #1f2636;
}
</style>
